import { Tactic } from 'generated/maya.types';

export type TacticActionOrderGroup = Record<string, Tactic[]>;

export const isOnlyOneTactic = (groupedTactics: TacticActionOrderGroup) => {
  return Object.values(groupedTactics).flat().length === 1;
};

export const isTacticGroupingImprovementsAvailable = ({
  enabled,
  dateAccepted,
  cepCreatedAt,
}: {
  enabled: boolean;
  dateAccepted?: string;
  cepCreatedAt?: string | null;
}) => {
  if (!enabled) {
    return false;
  }

  if (typeof dateAccepted !== 'string') {
    return enabled;
  }

  const cepCreatedAtDate = cepCreatedAt ? new Date(cepCreatedAt) : null;
  const [day, month, year] = dateAccepted.split('/').map(Number);
  const acceptedDate = new Date(year, month - 1, day);

  acceptedDate.setHours(0, 0, 0, 0);

  if (
    cepCreatedAtDate === null
      || Number.isNaN(cepCreatedAtDate.getTime())
      || Number.isNaN(acceptedDate.getTime())
      || cepCreatedAtDate.getTime() <= acceptedDate.getTime()
  ) {
    return false;
  }

  return true;
};

export const getSourceGroupUpdate = (
  draggableId: string,
  originalTactics: Tactic[],
) => {
  const draggedTacticOriginal = originalTactics.find((tactic) => tactic._id === draggableId);
  const sourceGroupId = draggedTacticOriginal?.parentTactic?._id;

  let tacticsToUngroup: Tactic[] = [];
  let groupToDeleteId: string | null = null;

  if (sourceGroupId) {
    const siblings = originalTactics.filter(
      (tactic) => tactic.parentTactic?._id === sourceGroupId && tactic._id !== draggableId,
    );

    if (siblings.length < 2) {
      groupToDeleteId = sourceGroupId;
      tacticsToUngroup = siblings;
    }
  }

  return {
    tacticsToUngroup,
    groupToDeleteId,
  };
};

export const groupTacticsByActionOrder = (tactics: Tactic[]): TacticActionOrderGroup => {
  return tactics.reduce<TacticActionOrderGroup>((acc, tactic) => {
    const actionOrder = tactic?.actionOrder;

    if (!actionOrder) {
      return acc;
    }

    const key = String(actionOrder);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(tactic);

    return acc;
  }, {});
};

export const createIdToStepMap = (groupedTactics: TacticActionOrderGroup) => {
  const idToStepMap = new Map<string, string>();

  Object.entries(groupedTactics).forEach(([step, tactics]) => {
    tactics.forEach((tactic) => {
      idToStepMap.set(tactic._id, step);

      if (tactic.parentTactic) {
        idToStepMap.set(tactic.parentTactic._id, step);
      }
    });
  });

  return idToStepMap;
};

export const getPaginatedGroupedTactics = ({
  groupedTactics,
  page,
  pageSize,
}: {
  groupedTactics: TacticActionOrderGroup;
  page: number;
  pageSize: number;
}): TacticActionOrderGroup => {
  const stepKeys = Object.keys(groupedTactics);
  const paginatedStepKeys = stepKeys.slice(page * pageSize, page * pageSize + pageSize);

  return paginatedStepKeys.reduce<TacticActionOrderGroup>((acc, key) => {
    acc[key] = groupedTactics[key];

    return acc;
  }, {});
};

export const getDependentStepIndexes = ({
  movedStepKey,
  orderedStepKeys,
  originalTactics,
  idToStepMap,
}: {
  movedStepKey: string;
  orderedStepKeys: string[];
  originalTactics: Tactic[];
  idToStepMap: Map<string, string>;
}) => {
  const dependentStepKeys = new Set<string>();

  originalTactics.forEach((tactic) => {
    const prevTacticId = tactic.previousTactic?._id;

    if (!prevTacticId) {
      return;
    }

    const previousStep = idToStepMap.get(prevTacticId);

    if (previousStep === movedStepKey && tactic.actionOrder != null) {
      dependentStepKeys.add(String(tactic.actionOrder));
    }
  });

  return orderedStepKeys
    .map((stepKey, index) => (dependentStepKeys.has(stepKey) ? index : -1))
    .filter((index) => index !== -1);
};

export type MovementCheck = {
  allowed: boolean;
  message?: string;
};

export const canMoveStep = ({
  movedStepKey,
  destinationIndex,
  orderedStepKeys,
  originalTactics,
  idToStepMap,
  dependencyBeforeMessage,
  dependencyAfterMessage,
}: {
  movedStepKey: string;
  destinationIndex: number;
  orderedStepKeys: string[];
  originalTactics: Tactic[];
  idToStepMap: Map<string, string>;
  dependencyBeforeMessage: string;
  dependencyAfterMessage: string;
}): MovementCheck => {
  const dependencyId = originalTactics.find(
    (tactic) => String(tactic.actionOrder) === movedStepKey,
  )?.previousTactic?._id;

  const dependencyStep = dependencyId ? idToStepMap.get(dependencyId) : null;

  if (dependencyStep) {
    const dependencyIndex = orderedStepKeys.indexOf(dependencyStep);
    const minAllowedIndex = dependencyIndex + 1;

    if (dependencyIndex !== -1 && destinationIndex < minAllowedIndex) {
      return {
        allowed: false,
        message: dependencyBeforeMessage,
      };
    }
  }

  const dependentStepIndexes = getDependentStepIndexes({
    movedStepKey,
    orderedStepKeys,
    originalTactics,
    idToStepMap,
  });

  if (dependentStepIndexes.length > 0) {
    const maxAllowedIndex = Math.min(...dependentStepIndexes) - 1;

    if (destinationIndex > maxAllowedIndex) {
      return {
        allowed: false,
        message: dependencyAfterMessage,
      };
    }
  }

  return { allowed: true };
};

export const canMoveTacticToStep = ({
  draggedTactic,
  destinationActionOrder,
  destinationIndex,
  isCreatingNewStep = false,
  originalTactics,
  idToStepMap,
  dependencyErrorMessage,
}: {
  draggedTactic: Tactic;
  destinationActionOrder: number;
  destinationIndex?: number;
  isCreatingNewStep?: boolean;
  originalTactics: Tactic[];
  idToStepMap: Map<string, string>;
  dependencyErrorMessage: string;
}): MovementCheck => {
  const groupedTactics = groupTacticsByActionOrder(originalTactics);
  const orderedStepKeys = Object.keys(groupedTactics).sort((a, b) => +a - +b);

  const sourceStepKey = String(draggedTactic.actionOrder);
  const destinationStepKey = String(destinationActionOrder);
  const destinationStepIndex = orderedStepKeys.indexOf(destinationStepKey);
  const targetDestinationIndex = isCreatingNewStep
    ? destinationIndex ?? destinationStepIndex
    : destinationStepIndex;

  const dependencyId = draggedTactic.previousTactic?._id;
  const dependencyStep = dependencyId ? idToStepMap.get(dependencyId) : null;

  if (dependencyStep) {
    const dependencyIndex = orderedStepKeys.indexOf(dependencyStep);
    const shouldShiftDependencyIndex = isCreatingNewStep
        && targetDestinationIndex !== -1
        && dependencyIndex !== -1
        && dependencyIndex >= targetDestinationIndex;

    const adjustedDependencyIndex = shouldShiftDependencyIndex
      ? dependencyIndex + 1
      : dependencyIndex;

    if (
      adjustedDependencyIndex !== -1
        && targetDestinationIndex !== -1
        && targetDestinationIndex < adjustedDependencyIndex
    ) {
      return {
        allowed: false,
        message: dependencyErrorMessage,
      };
    }
  }

  const dependentStepIndexes = getDependentStepIndexes({
    movedStepKey: sourceStepKey,
    orderedStepKeys,
    originalTactics,
    idToStepMap,
  });

  if (dependentStepIndexes.length > 0) {
    const maxAllowedIndex = Math.min(...dependentStepIndexes) - 1;

    if (targetDestinationIndex !== -1 && targetDestinationIndex > maxAllowedIndex) {
      return {
        allowed: false,
        message: dependencyErrorMessage,
      };
    }
  }

  return { allowed: true };
};

export const getUniqueTacticGroupTitle = ({
  groupTitles,
  groupNamePrefix,
}: {
  groupTitles: string[];
  groupNamePrefix: string;
}) => {
  const usedNumbers = groupTitles
    .map((title) => {
      const match = title.match(/(\d+)$/);

      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((number) => number > 0)
    .sort((a, b) => a - b);

  const nextNumber = usedNumbers.reduce((acc, current) => (
    current === acc ? acc + 1 : acc
  ), 1);

  return `${groupNamePrefix} ${nextNumber}`;
};

